import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Custom variants for accounting site
        cta: "gradient-cta text-primary-foreground shadow-lg hover:shadow-xl hover:-translate-y-0.5 rounded-full",
        "cta-outline": "border-2 border-secondary-foreground text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10 rounded-full",
        "hero-primary": "gradient-cta text-primary-foreground shadow-lg hover:shadow-xl hover:-translate-y-0.5 rounded-full px-6",
        "hero-secondary": "border-2 border-secondary-foreground/80 text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10 rounded-full px-6",
        "client-area": "bg-primary text-primary-foreground rounded-full hover:opacity-90 shadow-md hover:shadow-lg",
        nibo: "bg-orange-500 text-white hover:bg-orange-600 shadow-md hover:shadow-lg",
        cnd: "bg-white text-secondary border-2 border-secondary/20 hover:border-secondary/40 shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
